import { useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { Image, Skeleton, ActionButton } from "@react-spectrum/s2";

import { fetchPresignURL } from "@app/services/s3";

import { getPresignedImageUrl } from "@app/utils";

export const StoryImage = ({ visuals, idx = 0, styles }) => {
  const currentVisual = useMemo(() => visuals?.[idx], [visuals, idx]);

  const query = useQuery({
    queryKey: [
      "storyImage",
      currentVisual ? (currentVisual.path ?? currentVisual.id) : "default",
      currentVisual?.prompt,
    ],
    queryFn: async ({ signal }) => {
      if (currentVisual?.path) {
        const response = await fetchPresignURL(currentVisual.path);
        return response.presignURL;
      } else if (currentVisual?.id && currentVisual?.libraryId) {
        return getPresignedImageUrl(
          currentVisual?.libraryId,
          currentVisual?.id,
        );
      } else {
        return "https://react-spectrum.adobe.com/s2/preview.c3b340d3.png";
      }
    },
    retryDelay: (attemptIndex, error) => {
      // console.error("FireflyImage5 generation error:", error);
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    enabled:
      !!currentVisual?.path ||
      (!!currentVisual?.id && !!currentVisual?.libraryId),
    staleTime: 1 * 60 * 60 * 1000, // 1 hour
  });

  return (
    <>
      <Skeleton isLoading={query.isFetching}>
        <Image src={query.data} alt={currentVisual?.prompt} styles={styles} />
      </Skeleton>
      {query.status === "error" && (
        <ActionButton onPress={() => query.refetch()}>Retry</ActionButton>
      )}
    </>
  );
};

StoryImage.propTypes = {
  sourceReference: PropTypes.string.isRequired,
  prompt: PropTypes.string.isRequired,
  styles: PropTypes.object,
};
